import React from 'react';
import { CloseOutlined, ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { notification } from 'antd';
import { ReactComponent as CheckIcon } from './checkmark-circle-02.svg';
import { ReactComponent as AlertIcon } from './alert-02.svg';
import '../campaigns.scss';

const CAMPAIGN_TOAST_NOTICE_CLASS = 'campaign-notification-toast';

const CAMPAIGN_TOAST_ROOT = {
  borderRadius: 12,
  display: 'flex',
  width: 400,
  maxWidth: 'calc(100vw - 48px)',
  padding: '12px 16px',
  alignItems: 'center',
  gap: 16,
  overflow: 'hidden',
  boxShadow:
    '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
};

const CAMPAIGN_TOAST_TITLE = {
  color: '#fff',
  fontFamily: 'var(--ff_primary, inherit)',
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '24px',
};

const CAMPAIGN_TOAST_BG = {
  success: '#16a34a',
  error: '#e62e2e',
  info: '#1677ff',
  warning: '#d97706',
};

// AAPU-6711 — Lickly toasts use the DS status hues (kept semantic: green/red/blue/amber). Solid
// backgrounds carry white text + icons, so success/warning use the darker -600 step for contrast.
// RAD/Amplify keep the map above. Inline styles resolve var(), with concrete-hex fallbacks.
const LICKLY_TOAST_BG = {
  success: 'var(--ds-green-600, #2a8047)',
  error: 'var(--ds-danger-500, #e0493f)',
  info: 'var(--ds-info-500, #4783cc)',
  warning: 'var(--ds-warning-600, #c27b17)',
};

const isLicklyTenant = () =>
  typeof document !== 'undefined' &&
  document.documentElement.getAttribute('data-tenant') === 'lickly';

function wrapToastIcon(node, variant) {
  const monochrome = variant === 'success' ? { filter: 'brightness(0) invert(1)' } : undefined;
  return <span style={{ display: 'inline-flex', lineHeight: 0, ...monochrome }}>{node}</span>;
}

const NOTIFICATION_VARIANT_BY_TYPE = {
  success: 'success',
  error: 'error',
  info: 'info',
  warning: 'warning',
};

function notificationVariantForBackground(type) {
  return NOTIFICATION_VARIANT_BY_TYPE[type] || 'error';
}

function notificationIcon(type) {
  switch (type) {
    case 'success':
      return wrapToastIcon(<CheckIcon />, 'success');
    case 'info':
      return (
        <span style={{ display: 'inline-flex', lineHeight: 0 }}>
          <InfoCircleOutlined style={{ fontSize: 22, color: '#fff' }} />
        </span>
      );
    case 'warning':
      return (
        <span style={{ display: 'inline-flex', lineHeight: 0 }}>
          <ExclamationCircleOutlined style={{ fontSize: 22, color: '#fff' }} />
        </span>
      );
    default:
      return wrapToastIcon(<AlertIcon />, 'error');
  }
}

export const formatCurrency = (num) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  return formatter.format(num);
};

export function displayNotification(msg, type, time = 5) {
  const notificationType =
    type === 'success' || type === 'error' || type === 'info' || type === 'warning'
      ? type
      : 'error';
  const variant = notificationVariantForBackground(notificationType);
  const toastBg = isLicklyTenant() ? LICKLY_TOAST_BG : CAMPAIGN_TOAST_BG;

  notification[notificationType]({
    title: msg,
    duration: time,
    placement: 'topRight',
    className: CAMPAIGN_TOAST_NOTICE_CLASS,
    closeIcon: <CloseOutlined style={{ color: '#fff' }} />,
    icon: notificationIcon(notificationType),
    styles: {
      root: {
        ...CAMPAIGN_TOAST_ROOT,
        background: toastBg[variant],
      },
      title: CAMPAIGN_TOAST_TITLE,
    },
  });
}
